// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ALL_REQUEST_TYPES,
  REQUEST_TYPES_OFFERED_ONLY_WHERE_DECLARED,
  isRequestTypeHandledInLegalMatters,
} from '@vn-grc/shared';
import type { LegalMatterRightOption, PublicIntakePayload } from '@vn-grc/shared';
import { apiClient } from '../../services/api-client.js';
import { useLocale } from '../../hooks/useLocale.js';
import { useInstalledLanguages } from '../../hooks/useInstalledLanguages.js';
import { usePageTitle } from '../../hooks/usePageTitle.js';
import { buildCountryOptions } from '../../utils/country-options.js';
import { extractErrorMessage } from '../../utils/api-helpers.js';
import { formatTemplate, tKey, tPath } from '../../utils/i18n-helpers.js';

/**
 * Public intake form for data subject rights requests.
 * Accessible without authentication.
 *
 * Phase 9 extension: supports ?key=<intake_key> query param for
 * deterministic org routing. When a key is provided, the form loads
 * the intake config to get org context and enabled request types.
 *
 * See: docs/product/phases/phase-1/sprint-1.0.0.md - Epic A
 */
/** The fields a refusal may name, in the order the form shows them (P2-F6). */
const FIELD_ORDER = [
  'requester_name',
  'requester_email',
  'requester_phone',
  'country',
  'request_type',
  'legal_matter_right_code',
  'description',
  'consent_to_processing',
] as const;

export function IntakeFormPage() {
  usePageTitle('Intake Form');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const intakeKey = searchParams.get('key') || undefined;
  const { locale, setLocale, t } = useLocale();
  const { languages } = useInstalledLanguages();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  /*
   * Sprint 63.31.0 (gate E12.5 pass 2, P2-F6; WCAG 3.3.1, 4.1.3) — a refusal
   * the server states after submit is announced by an assertive summary and
   * moves focus to the first field it names. Bumped on every refusal so the
   * same refusal twice still moves focus.
   */
  const [refusalSeq, setRefusalSeq] = useState(0);
  const summaryRef = useRef<HTMLDivElement>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);
  const [orgName, setOrgName] = useState<string | null>(null);
  const [allowedRequestTypes, setAllowedRequestTypes] = useState<string[] | null>(null);
  /*
   * Sprint 63.31.0 (owner ruling OD-E) — for a request type handled in Legal
   * Matters, the installed rights the requester may raise, from the intake
   * config. Labelled by the pack's own `name_key`. The question is never a
   * condition of being heard (gate E12.5 pass 2, P2-L2): left unanswered, the
   * organisation records the right on the legal matter.
   */
  const [legalMatterRights, setLegalMatterRights] = useState<
    Record<string, LegalMatterRightOption[]>
  >({});

  /** Extract short language code from locale (e.g. 'sv-SE' → 'sv'). */
  const shortLang = locale.split('-')[0];

  // The 249 ISO 3166-1 alpha-2 codes, named in the reader's locale.
  const countryOptions = useMemo(() => buildCountryOptions(locale), [locale]);

  const [form, setForm] = useState<
    PublicIntakePayload & {
      customer_reference?: string;
      country?: string;
      preferred_response_channel?: string;
    }
  >({
    request_type: '' as any,
    requester_name: '',
    requester_email: '',
    requester_phone: '',
    customer_reference: '',
    country: '',
    preferred_response_channel: 'email',
    description: '',
    preferred_language: shortLang,
    consent_to_processing: false,
    legal_matter_right_code: '',
  });

  // Load intake config: by key if provided, otherwise auto-resolve from backend
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = intakeKey
          ? await apiClient.getPublicIntakeConfig(intakeKey)
          : await apiClient.getDefaultPublicIntakeConfig();
        if (config) {
          setOrgName(config.org_name);
          if (config.enabled_request_types?.length) {
            setAllowedRequestTypes(config.enabled_request_types);
          }
          if (config.legal_matter_rights && typeof config.legal_matter_rights === 'object') {
            setLegalMatterRights(config.legal_matter_rights);
          }
          if (config.default_language) {
            setForm((prev) => ({ ...prev, preferred_language: config.default_language }));
            // Only switch the UI locale when navigating via an explicit intake key.
            // Auto-resolve should not override the user's current browser locale.
            if (intakeKey) {
              const matchedLocale = languages.find((o) =>
                o.value.toLowerCase().startsWith(config.default_language.toLowerCase() + '-'),
              );
              if (matchedLocale) setLocale(matchedLocale.value);
            }
          }
        }
      } catch {
        if (intakeKey) {
          setConfigError(
            ((t.intake as Record<string, unknown>).config_load_error as string) ??
              'Unable to load intake configuration. Please check the URL.',
          );
        }
      } finally {
        setConfigLoading(false);
      }
    };
    loadConfig();
  }, [intakeKey]);

  const handleChange = (field: keyof PublicIntakePayload, value: unknown) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      const routed = isRequestTypeHandledInLegalMatters(form.request_type);
      const result = await apiClient.submitIntake(
        {
          ...form,
          // OD-E — sent only for a request type handled in Legal Matters.
          legal_matter_right_code: routed ? form.legal_matter_right_code || null : undefined,
        },
        intakeKey,
      );
      navigate('/rights-request/confirmation', {
        state: {
          referenceNumber: result.reference_number,
          language: form.preferred_language,
          handledIn: result.handled_in ?? null,
        },
      });
    } catch (err: any) {
      if (err?.details?.issues) {
        const fieldErrors: Record<string, string> = {};
        for (const issue of err.details.issues) {
          fieldErrors[issue.path] = issue.message;
        }
        setErrors(fieldErrors);
      } else if (err?.serverCode === 'REQUEST_TYPE_NOT_OFFERED') {
        // Sprint 63.31.0 (#438) — a type no installed framework grants (422).
        setErrors({ request_type: t.intake.request_type_not_offered });
      } else if (err?.serverCode === 'request_type_not_enabled') {
        setErrors({ request_type: t.intake.request_type_not_enabled });
      } else {
        // Any other refusal was dropped silently: the button re-enabled and
        // nothing on the page said the request had not been sent.
        setErrors({
          _form: formatTemplate(t.intake.submit_failed, {
            reason: extractErrorMessage(err, t.common.error),
          }),
        });
      }
      setRefusalSeq((n) => n + 1);
    } finally {
      setIsSubmitting(false);
    }
  };

  // P2-F6 — after a refusal, focus the first field it names (in form order);
  // a refusal that names no field focuses the summary, which is announced.
  useEffect(() => {
    if (refusalSeq === 0) return;
    const firstField = FIELD_ORDER.find((field) => errors[field]);
    const target = firstField ? document.getElementById(firstField) : null;
    if (target) target.focus();
    else summaryRef.current?.focus();
    // `errors` is read for this refusal only; a later keystroke clears its own
    // field and must not move focus.
  }, [refusalSeq]);

  // The config's list is the enabled types the organisation's installed
  // frameworks grant (Sprint 63.31.0, #438; filtered by the server). With no
  // list, the vocabulary less the types that exist only where a framework
  // declares them: offering one with nothing installed would claim a right.
  const displayRequestTypes =
    allowedRequestTypes ||
    ALL_REQUEST_TYPES.filter(
      (type) => !(REQUEST_TYPES_OFFERED_ONLY_WHERE_DECLARED as readonly string[]).includes(type),
    );

  const intakeT = t.intake as Record<string, unknown>;
  const intakeChannels = (intakeT.channels ?? {}) as Record<string, string>;

  // OD-E — offered only where several installed rights rest on the provision;
  // with one, the server records that one, and the form asks nothing.
  const routedRights = isRequestTypeHandledInLegalMatters(form.request_type)
    ? (legalMatterRights[form.request_type] ?? [])
    : [];
  const rightLabel = (right: LegalMatterRightOption) =>
    tPath(
      t,
      right.name_key,
      tKey(t.framework_rights as unknown as Record<string, unknown>, right.code),
    );

  const fieldLabels: Record<string, string> = {
    requester_name: t.intake.fields.full_name,
    requester_email: t.intake.fields.email,
    requester_phone: t.intake.fields.phone,
    // fix-sprint-20261026-0900 F10 — a refusal naming `country` is labelled too.
    country: (intakeT.country as string) ?? 'Country / Jurisdiction',
    request_type: t.intake.fields.request_type,
    legal_matter_right_code: t.intake.legal_matter_right,
    description: t.intake.fields.description,
    consent_to_processing: t.intake.fields.consent,
  };
  const summaryItems = Object.entries(errors).filter(
    ([field, message]) => message && field !== '_form',
  );

  if (configLoading) {
    return (
      <div className="intake-form-page">
        <div className="intake-form-container">
          <p>{t.common.loading}</p>
        </div>
      </div>
    );
  }

  if (configError) {
    return (
      <div className="intake-form-page">
        <div className="intake-form-container">
          <h1>{(intakeT.error_title as string) ?? 'Error'}</h1>
          <p>{configError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="intake-form-page">
      <div className="intake-form-container">
        {/* Language selector */}
        <div className="language-selector">
          {/* fix-sprint-20261026-0900 F12 — the picker had no accessible name. */}
          <select
            aria-label={t.common.select_language}
            data-testid="intake-language-select"
            value={locale}
            onChange={(e) => {
              const newLocale = e.target.value;
              setLocale(newLocale);
              handleChange('preferred_language', newLocale.split('-')[0]);
            }}
          >
            {languages.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <h1>{t.intake.title}</h1>
        {orgName && (
          <p className="intake-org-name">
            {(intakeT.organization_label as string) ?? 'Organization'}: <strong>{orgName}</strong>
          </p>
        )}
        <p>{t.intake.subtitle}</p>

        {refusalSeq > 0 && (summaryItems.length > 0 || errors._form) && (
          <div
            className="intake-error-summary"
            role="alert"
            tabIndex={-1}
            ref={summaryRef}
            data-testid="intake-error-summary"
          >
            {errors._form && <p data-testid="intake-submit-error">{errors._form}</p>}
            {summaryItems.length > 0 && (
              <p className="intake-error-summary__title">{t.intake.error_summary}</p>
            )}
            {summaryItems.length > 0 && (
              <ul>
                {summaryItems.map(([field, message]) => (
                  <li key={field}>
                    <a href={`#${field}`}>
                      {fieldLabels[field] ? `${fieldLabels[field]}: ` : ''}
                      {message}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Full Name */}
          <div className="form-field">
            <label htmlFor="requester_name">{t.intake.fields.full_name} *</label>
            <input
              id="requester_name"
              type="text"
              value={form.requester_name}
              onChange={(e) => handleChange('requester_name', e.target.value)}
              required
              aria-invalid={errors.requester_name ? true : undefined}
              aria-describedby={errors.requester_name ? 'requester_name_error' : undefined}
            />
            {errors.requester_name && (
              <span className="error" id="requester_name_error">
                {errors.requester_name}
              </span>
            )}
          </div>

          {/* Email */}
          <div className="form-field">
            <label htmlFor="requester_email">{t.intake.fields.email} *</label>
            <input
              id="requester_email"
              type="email"
              value={form.requester_email}
              onChange={(e) => handleChange('requester_email', e.target.value)}
              required
              aria-invalid={errors.requester_email ? true : undefined}
              aria-describedby={errors.requester_email ? 'requester_email_error' : undefined}
            />
            {errors.requester_email && (
              <span className="error" id="requester_email_error">
                {errors.requester_email}
              </span>
            )}
          </div>

          {/* Phone */}
          <div className="form-field">
            <label htmlFor="requester_phone">{t.intake.fields.phone}</label>
            <input
              id="requester_phone"
              type="tel"
              value={form.requester_phone ?? ''}
              onChange={(e) => handleChange('requester_phone', e.target.value || null)}
              aria-invalid={errors.requester_phone ? true : undefined}
              aria-describedby={errors.requester_phone ? 'requester_phone_error' : undefined}
            />
            {errors.requester_phone && (
              <span className="error" id="requester_phone_error">
                {errors.requester_phone}
              </span>
            )}
          </div>

          {/* Customer Reference */}
          <div className="form-field">
            <label htmlFor="customer_reference">
              {(intakeT.customer_reference as string) ?? 'Customer Reference Number'}
            </label>
            <input
              id="customer_reference"
              type="text"
              value={form.customer_reference ?? ''}
              onChange={(e) => setForm((prev) => ({ ...prev, customer_reference: e.target.value }))}
              maxLength={100}
            />
          </div>

          {/* Country / Jurisdiction */}
          <div className="form-field">
            <label htmlFor="country">
              {(intakeT.country as string) ?? 'Country / Jurisdiction'}
            </label>
            {/*
             * A free-text `<input maxLength={100}>` stood here, while
             * `publicIntakeSchema` demands an ISO 3166-1 alpha-2 code — so a
             * data subject typing "Vietnam", which this label invites, got a
             * `400 validation_error` in English on a public, unauthenticated
             * form with no way to find out what was wanted.
             *
             * A native `<select>` rather than the admin `PortalSelect`: every
             * other control on this page is native, no public page loads
             * react-select, and the wrapper's presets are the admin form styles
             * (`adminFormSelectStyles`), which this page does not carry.
             *
             * The empty option is the default and submits `country: ''`; the
             * schema reads a blank field as "not stated" and stores null.
             */}
            <select
              id="country"
              value={form.country ?? ''}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, country: e.target.value }));
                setErrors((prev) => ({ ...prev, country: '' }));
              }}
              aria-invalid={errors.country ? true : undefined}
              aria-describedby={errors.country ? 'country_error' : undefined}
            >
              <option value="">{t.common.select_placeholder}</option>
              {countryOptions.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
            {errors.country && (
              <span className="error" id="country_error">
                {errors.country}
              </span>
            )}
          </div>

          {/* Preferred Response Channel */}
          <div className="form-field">
            <label htmlFor="preferred_response_channel">
              {(intakeT.preferred_channel as string) ?? 'Preferred Response Channel'}
            </label>
            <select
              id="preferred_response_channel"
              value={form.preferred_response_channel ?? 'email'}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, preferred_response_channel: e.target.value }))
              }
            >
              <option value="email">{intakeChannels.email ?? 'Email'}</option>
              <option value="postal">{intakeChannels.postal ?? 'Postal'}</option>
              <option value="phone">{intakeChannels.phone ?? 'Phone'}</option>
              <option value="portal">{intakeChannels.portal ?? 'Portal'}</option>
            </select>
          </div>

          {/* Request Type */}
          <div className="form-field">
            <label htmlFor="request_type">{t.intake.fields.request_type} *</label>
            <select
              id="request_type"
              value={form.request_type}
              onChange={(e) => handleChange('request_type', e.target.value)}
              required
              aria-invalid={errors.request_type ? true : undefined}
              aria-describedby={errors.request_type ? 'request_type_error' : undefined}
            >
              <option value="">--</option>
              {displayRequestTypes.map((type) => (
                <option key={type} value={type}>
                  {(t.request_types as Record<string, string>)[type] || type}
                </option>
              ))}
            </select>
            {errors.request_type && (
              <span
                className="error"
                id="request_type_error"
                data-testid="intake-request-type-error"
              >
                {errors.request_type}
              </span>
            )}
          </div>

          {/* Sprint 63.31.0 (OD-E) — which right a request handled in Legal Matters raises. */}
          {routedRights.length > 1 && (
            <div className="form-field" data-testid="intake-legal-matter-right">
              <label htmlFor="legal_matter_right_code">{t.intake.legal_matter_right}</label>
              <p className="dm-form-hint" id="legal_matter_right_hint">
                {t.intake.legal_matter_right_hint}
              </p>
              <select
                id="legal_matter_right_code"
                value={form.legal_matter_right_code ?? ''}
                onChange={(e) => handleChange('legal_matter_right_code', e.target.value)}
                aria-invalid={errors.legal_matter_right_code ? true : undefined}
                aria-describedby={
                  errors.legal_matter_right_code
                    ? 'legal_matter_right_hint legal_matter_right_error'
                    : 'legal_matter_right_hint'
                }
              >
                <option value="">{t.intake.legal_matter_right_not_sure}</option>
                {routedRights.map((right) => (
                  <option key={right.code} value={right.code}>
                    {rightLabel(right)}
                  </option>
                ))}
              </select>
              {errors.legal_matter_right_code && (
                <span className="error" id="legal_matter_right_error">
                  {errors.legal_matter_right_code}
                </span>
              )}
            </div>
          )}

          {/* Description */}
          <div className="form-field">
            <label htmlFor="description">{t.intake.fields.description} *</label>
            <textarea
              id="description"
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={5}
              maxLength={5000}
              required
              aria-invalid={errors.description ? true : undefined}
              aria-describedby={errors.description ? 'description_error' : undefined}
            />
            {errors.description && (
              <span className="error" id="description_error">
                {errors.description}
              </span>
            )}
          </div>

          {/* Consent */}
          <div className="form-field form-field--checkbox">
            <label>
              <input
                id="consent_to_processing"
                type="checkbox"
                checked={form.consent_to_processing}
                onChange={(e) => handleChange('consent_to_processing', e.target.checked)}
                required
                aria-invalid={errors.consent_to_processing ? true : undefined}
                aria-describedby={
                  errors.consent_to_processing ? 'consent_to_processing_error' : undefined
                }
              />
              {t.intake.fields.consent} *
            </label>
            {errors.consent_to_processing && (
              <span className="error" id="consent_to_processing_error">
                {errors.consent_to_processing}
              </span>
            )}
          </div>

          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? t.common.loading : t.intake.submit}
          </button>
        </form>
      </div>
    </div>
  );
}
